import React, { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { useLanguageContext } from 'store/language';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import API from 'core/Api';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import CreateEntity from 'components/crud/CreateEntity';
import { useModal } from 'store/modal';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { FaTimes, FaCheck } from 'react-icons/fa';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Button from 'components/Button';

interface ISelectOptionProps {
  label: string;
  value: number;
}

interface IUserOptionProps extends ISelectOptionProps {
  groups: number[];
  status: string;
}

interface IInputs {
  is_mass_training: boolean;
  facilitator_id: number | null;
  type: string;
  user_id?: number | null;
  groups?: ISelectOptionProps[];
  excluded_user_ids?: number[];
}

const CreateForm: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { showModal } = useModal();
  const history = useHistory();

  const methods = useForm<IInputs>({
    defaultValues: {
      is_mass_training: false,
      facilitator_id: null,
      type: '',
      user_id: null,
      groups: [] as ISelectOptionProps[],
      excluded_user_ids: [],
    },
  });

  const isMassTraining = methods.watch('is_mass_training');
  const selectedGroups = methods.watch('groups') || [];
  const [excludedEmployees, setExcludedEmployees] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSelectedActive, setHasSelectedActive] = useState(false);

  const { isLoading: isLoadingDropdowns, data: dropdowns } = useQuery<
    { user: IUserOptionProps[]; facilitator: ISelectOptionProps[]; group: ISelectOptionProps[] },
    Error
  >(
    [buildClientEntity('health-and-safety-training-dropdowns', client)],
    (): Promise<{ user: IUserOptionProps[]; facilitator: ISelectOptionProps[]; group: ISelectOptionProps[] }> => {
      return API.get('health-and-safety-training/dropdown-options?all-users=true').then(res => {
        return res.data?.[language || 'bg'] || {};
      });
    },
    {
      cacheTime: 0,
    }
  );

  const FormDropdowns = useMemo(() => {
    return {
      users: dropdowns?.user || [],
      facilitators: dropdowns?.facilitator || [],
      trainingTypes: [
        { value: 'initial', label: t('healthAndSafetyTraining.types.initial') },
        { value: 'periodic', label: t('healthAndSafetyTraining.types.periodic') },
        { value: 'emergency', label: t('healthAndSafetyTraining.types.emergency') },
        { value: 'at_workplace', label: t('healthAndSafetyTraining.types.at_workplace') },
      ],
      groups: dropdowns?.group || [],
    };
  }, [dropdowns, t]);

  const usersInGroups: IUserOptionProps[] = useMemo(() => {
    const selectedGroupIds = selectedGroups.map(g => g.value);

    return FormDropdowns.users.filter(user => user.groups.some(groupId => selectedGroupIds.includes(groupId)));
  }, [selectedGroups, FormDropdowns.users]);

  const hasInactiveEmployees = useMemo(() => usersInGroups.some(user => user.status !== 'active'), [usersInGroups]);

  const toggleExclusion = (userId: number) => {
    setExcludedEmployees(prev => (prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]));
  };

  const handleExcludeActive = () => {
    const currentGroups = methods.getValues('groups') || [];
    const selectedGroupIds = currentGroups.map(g => String(g.value));
    const hasNoGroupSelected = selectedGroupIds.includes('null');

    const filteredUsers = FormDropdowns.users.filter(user => {
      const isInSelectedGroup = user.groups.map(gid => String(gid)).some(groupId => selectedGroupIds.includes(groupId));
      const isNoGroupUser = hasNoGroupSelected && user.groups.length === 0;
      return isInSelectedGroup || isNoGroupUser;
    });

    const excludedEmpIds = filteredUsers.filter(user => user.status !== 'active').map(user => user.value);

    setExcludedEmployees(excludedEmpIds);
    setHasSelectedActive(true);
  };

  useEffect(() => {
    if (selectedGroups.length) {
      setHasSelectedActive(false);
    }
  }, [selectedGroups]);

  useEffect(() => {
    if (selectedGroups.length) {
      const userValues = usersInGroups.map(user => user.value);
      const shouldFilter = excludedEmployees.some(id => !userValues.includes(id));
      if (shouldFilter) {
        setExcludedEmployees(prev => prev.filter(id => userValues.includes(id)));
      }
    }
  }, [usersInGroups, excludedEmployees, selectedGroups]);

  return (
    <Row>
      <Col className="col-sm-6">
        <CreateEntity
          entity="health-and-safety-training"
          methods={methods}
          queryKey={buildClientEntity('health-and-safety-training', client)}
          onSuccess={() => {
            showModal({
              type: 'success',
              content: isMassTraining ? t('healthAndSafetyTraining.massSuccess') : t('healthAndSafetyTraining.success'),
            });
            methods.reset();
            const url = buildClientUrl('/health-and-safety-training', client);
            history.push(url);
          }}
        >
          {(create: any) => (
            <Form
              title={t('healthAndSafetyTraining.create')}
              methods={methods}
              btnLabel={t('common.create')}
              primaryActionBtnType="primary"
              isUploading={isSubmitting}
              customError
              onSubmit={(data: any) => {
                showModal({
                  type: 'confirm',
                  heading: t('common.confirm'),
                  content: isMassTraining
                    ? t('healthAndSafetyTraining.confirmMassCreate')
                    : t('healthAndSafetyTraining.confirmCreate'),
                  onConfirm: async () => {
                    setIsSubmitting(true);
                    try {
                      const formattedData = {
                        is_mass_training: data.is_mass_training,
                        facilitator_id: data.facilitator_id?.value,
                        type: data.type.value,
                        ...(data.is_mass_training
                          ? {
                              groups: data.groups?.map((g: ISelectOptionProps) => g.value),
                              excluded_user_ids: excludedEmployees,
                            }
                          : { user_id: data.user_id?.value }),
                      };

                      await create(formattedData);
                    } catch (error: any) {
                      showModal({
                        type: 'error',
                        content: <>{error.data.errors_common}</>,
                      });
                    } finally {
                      setIsSubmitting(false);
                      const url = buildClientUrl('/health-and-safety-training', client);
                      history.push(url);
                    }
                  },
                  confirmLabel: t('common.confirm'),
                });
              }}
            >
              <div className="d-flex align-items-center">
                <h6 className="mr-2">{t('healthAndSafetyTraining.isMassTraining')}</h6>
                <FormGroup
                  controlId="formGroupIsMass"
                  checkType="switch"
                  name="is_mass_training"
                  isHorizontal
                />
              </div>

              <Row className="mt-1">
                <Col md={{ span: 6, offset: 0 }}>
                  <FormGroup
                    controlId="formGroupFacilitator"
                    label={t('healthAndSafetyTraining.facilitator')}
                    type="select"
                    as="select"
                    name="facilitator_id"
                    isRequired
                    selectOptions={FormDropdowns.facilitators}
                  />
                </Col>

                <Col md={{ span: 6, offset: 0 }}>
                  <FormGroup
                    controlId="formGroupType"
                    label={t('healthAndSafetyTraining.type')}
                    type="select"
                    as="select"
                    name="type"
                    isRequired
                    selectOptions={FormDropdowns.trainingTypes}
                  />
                </Col>
              </Row>

              {!isMassTraining && (
                <FormGroup
                  controlId="formGroupUsers"
                  label={t('healthAndSafetyTraining.user')}
                  type="select"
                  as="select"
                  name="user_id"
                  isRequired
                  selectOptions={FormDropdowns.users}
                  isDisabled={isMassTraining}
                />
              )}

              {isMassTraining && (
                <>
                  <FormGroup
                    controlId="formGroupGroups"
                    label={t('healthAndSafetyTraining.groups')}
                    type="select"
                    as="select"
                    name="groups"
                    isRequired
                    isMulti
                    selectOptions={FormDropdowns.groups}
                  />

                  {hasInactiveEmployees && selectedGroups.length > 0 && !hasSelectedActive && (
                    <Row className="my-2">
                      <Col md={{ span: 12 }}>
                        <Button
                          btnType="primary"
                          onClick={handleExcludeActive}
                        >
                          {t('healthAndSafetyTraining.selectOnlyActive')}
                        </Button>
                      </Col>
                    </Row>
                  )}

                  {usersInGroups.length > 0 && <h6>{t('healthAndSafetyTraining.selectedUsers')}:</h6>}
                  {usersInGroups.map(user => (
                    <OverlayTrigger
                      key={user.value}
                      placement="bottom"
                      overlay={
                        <Tooltip id="tooltip-bottom">
                          {excludedEmployees.includes(user.value)
                            ? t('healthAndSafetyTraining.includeUser')
                            : t('healthAndSafetyTraining.excludeUser')}
                        </Tooltip>
                      }
                    >
                      <Badge
                        pill
                        variant={excludedEmployees.includes(user.value) ? 'danger' : 'primary'}
                        className="mr-2 mb-2"
                        onClick={() => toggleExclusion(user.value)}
                        style={{ cursor: 'pointer', padding: '8px', fontSize: '12px' }}
                      >
                        {user.label}
                        {excludedEmployees.includes(user.value) ? (
                          <FaTimes className="ml-2" />
                        ) : (
                          <FaCheck className="ml-2" />
                        )}
                      </Badge>
                    </OverlayTrigger>
                  ))}
                </>
              )}
            </Form>
          )}
        </CreateEntity>
      </Col>
    </Row>
  );
};

export default CreateForm;
